(() => {
  const normalizeText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/[’']/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const slugify = (value) =>
    normalizeText(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  class EvolvedSupportFaq extends HTMLElement {
    connectedCallback() {
      this.disconnect();

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.searchInput = this.querySelector('[data-evolved-faq-search]');
      this.clearButton = this.querySelector('[data-evolved-faq-clear]');
      this.status = this.querySelector('[data-evolved-faq-status]');
      this.noResults = this.querySelector('[data-evolved-faq-empty]');
      this.resultsRegion = this.querySelector('[data-evolved-faq-results]');
      this.items = Array.from(this.querySelectorAll('[data-evolved-faq-item]'));
      this.groups = Array.from(this.querySelectorAll('[data-evolved-faq-group]'));
      this.filters = Array.from(this.querySelectorAll('[data-evolved-faq-filter]'));
      this.activeCategory = 'all';
      this.desktopQuery = window.matchMedia('(min-width: 1150px)');

      this.querySelectorAll('[data-evolved-faq-tools]').forEach((element) => {
        element.hidden = false;
      });
      this.classList.add('is-enhanced');

      this.items.forEach((item) => {
        item.dataset.normalizedSearch = normalizeText(
          `${item.dataset.searchTerms || ''} ${item.textContent || ''}`
        );

        item.addEventListener(
          'toggle',
          () => {
            if (!item.open) return;

            this.items.forEach((otherItem) => {
              if (otherItem !== item && otherItem.open) otherItem.open = false;
            });
          },
          { signal }
        );

        item.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Escape' || !item.open) return;
            item.open = false;
            item.querySelector('summary')?.focus();
          },
          { signal }
        );
      });

      this.filters.forEach((filter) => {
        filter.addEventListener(
          'click',
          () => {
            this.setCategory(filter.dataset.evolvedFaqFilter || 'all');
          },
          { signal }
        );
      });

      this.desktopQuery.addEventListener(
        'change',
        (event) => {
          if (event.matches && this.activeCategory !== 'all') {
            this.setCategory('all', false);
          }
        },
        { signal }
      );

      this.searchInput?.addEventListener(
        'input',
        () => {
          if (normalizeText(this.searchInput.value) && this.activeCategory !== 'all') {
            this.setCategory('all', false);
            return;
          }
          this.applyFilters();
        },
        { signal }
      );
      this.searchInput?.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !this.searchInput.value) return;
          this.searchInput.value = '';
          this.applyFilters();
        },
        { signal }
      );

      this.clearButton?.addEventListener(
        'click',
        () => {
          this.searchInput.value = '';
          this.searchInput.focus();
          this.applyFilters();
        },
        { signal }
      );

      window.addEventListener('hashchange', () => this.openHashTarget(), { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const item = event.target.closest('[data-evolved-faq-item]');
        if (!item) return;
        if (this.searchInput) this.searchInput.value = '';
        this.setCategory('all', false);
        item.open = true;
      }, { signal });

      this.applyFilters();
      this.openHashTarget();
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      this.abortController?.abort();
      this.abortController = null;
    }

    setCategory(category, announce = true) {
      this.activeCategory = category;
      this.filters.forEach((candidate) => {
        candidate.setAttribute(
          'aria-pressed',
          String(candidate.dataset.evolvedFaqFilter === category)
        );
      });
      this.applyFilters(announce);
    }

    applyFilters(announce = true) {
      const query = normalizeText(this.searchInput?.value);
      const terms = query.split(' ').filter(Boolean).slice(0, 12);
      let visibleCount = 0;

      this.items.forEach((item) => {
        const matchesCategory =
          this.activeCategory === 'all' || item.dataset.category === this.activeCategory;
        const matchesSearch = terms.every((term) => item.dataset.normalizedSearch.includes(term));
        const isVisible = matchesCategory && matchesSearch;

        item.hidden = !isVisible;
        if (!isVisible && item.open) item.open = false;
        if (isVisible) visibleCount += 1;
        this.highlightMatches(item, isVisible ? terms : []);
      });

      this.groups.forEach((group) => {
        const visibleItems = Array.from(
          group.querySelectorAll('[data-evolved-faq-item]')
        ).filter((item) => !item.hidden);
        group.hidden = visibleItems.length === 0;

        const count = group.querySelector('[data-evolved-faq-group-count]');
        if (count) count.textContent = String(visibleItems.length);
      });

      if (this.clearButton) this.clearButton.hidden = !query;
      if (this.noResults) this.noResults.hidden = visibleCount > 0;
      if (this.resultsRegion) {
        this.resultsRegion.dataset.empty = String(visibleCount === 0);
        this.resultsRegion.dataset.category = this.activeCategory;
        this.resultsRegion.dataset.searching = String(Boolean(query));
      }

      if (this.status) {
        const noun = visibleCount === 1 ? 'answer' : 'answers';
        const activeFilter = this.filters.find(
          (filter) => filter.dataset.evolvedFaqFilter === this.activeCategory
        );
        const categoryLabel = activeFilter?.dataset.filterLabel || 'All topics';

        if (query) {
          this.status.textContent = `${visibleCount} ${noun} for “${this.searchInput.value.trim()}”`;
        } else if (this.activeCategory !== 'all') {
          this.status.textContent = `${visibleCount} ${categoryLabel.toLocaleLowerCase()} ${noun}`;
        } else {
          this.status.textContent = `${visibleCount} ${noun}`;
        }

        if (!announce) this.status.setAttribute('aria-live', 'off');
        requestAnimationFrame(() => this.status?.setAttribute('aria-live', 'polite'));
      }
    }

    highlightMatches(item, terms) {
      item.querySelectorAll('mark[data-evolved-match]').forEach((mark) => {
        const parent = mark.parentNode;
        mark.replaceWith(document.createTextNode(mark.textContent));
        parent.normalize();
      });
      if (!terms.length) return;
      const expression = new RegExp(terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'gi');
      item.querySelectorAll('.evolved-support__faq-summary > span:first-child, .evolved-support__faq-answer-inner').forEach((root) => {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);
        nodes.forEach((node) => {
          if (node.parentElement.closest('script, style, mark')) return;
          const matches = Array.from(node.textContent.matchAll(expression));
          if (!matches.length) return;
          const fragment = document.createDocumentFragment();
          let offset = 0;
          matches.forEach((match) => {
            fragment.append(document.createTextNode(node.textContent.slice(offset, match.index)));
            const mark = document.createElement('mark');
            mark.dataset.evolvedMatch = '';
            mark.textContent = match[0];
            fragment.append(mark);
            offset = match.index + match[0].length;
          });
          fragment.append(document.createTextNode(node.textContent.slice(offset)));
          node.replaceWith(fragment);
        });
      });
    }

    openHashTarget() {
      const hash = window.location.hash.slice(1);
      if (!hash) return;

      const target = this.querySelector(`#${CSS.escape(hash)}`);
      if (!(target instanceof HTMLDetailsElement)) return;

      this.setCategory('all', false);
      if (this.searchInput) this.searchInput.value = '';
      this.applyFilters(false);
      target.open = true;
      target.scrollIntoView({ block: 'start' });
    }
  }

  class EvolvedSupportContact extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.topicSelect = this.querySelector('[data-evolved-contact-topic]');
      this.topicHint = this.querySelector('[data-evolved-contact-hint]');
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[data-evolved-contact-submit]');

      const topics = { order: 'Order status', delivery: 'Delivery issue', return: 'Return or refund', compatibility: 'Compatibility question', product: 'Product issue' };
      const requestedTopic = topics[new URLSearchParams(window.location.search).get('topic')];
      if (requestedTopic && this.topicSelect && !this.topicSelect.value) this.topicSelect.value = requestedTopic;

      this.topicSelect?.addEventListener('change', () => this.updateTopicHint(), { signal });

      this.form?.addEventListener(
        'submit',
        () => {
          if (!this.form.checkValidity() || !this.submitButton) return;
          this.submitButton.setAttribute('aria-busy', 'true');
          clearTimeout(this.submitTimer);
          this.submitTimer = setTimeout(() => this.resetSubmit(), 10000);
        },
        { signal }
      );

      this.updateTopicHint();
      window.addEventListener('pageshow', () => this.resetSubmit(), { signal });

      const success = this.querySelector('[data-evolved-contact-success]');
      const errors = this.querySelector('[data-evolved-contact-errors]');
      if (success || errors) requestAnimationFrame(() => (errors || success).focus());
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      this.abortController?.abort();
      this.abortController = null;
      clearTimeout(this.submitTimer);
    }

    resetSubmit() {
      this.submitButton?.removeAttribute('aria-busy');
    }

    updateTopicHint() {
      if (!this.topicHint || !this.topicSelect) return;

      const hints = {
        'Order status': 'Include your order number. For the fastest update, check Track Order first.',
        'Delivery issue': 'Include your order number, delivery country, and the latest tracking message.',
        'Return or refund': 'Include your order number and confirm whether the item is unopened, defective, or incorrect.',
        'Compatibility question': 'List every device model, case, watch, and adapter you plan to use.',
        'Product issue': 'Include the product, device model, adapter details, and troubleshooting already tried.',
        Other: 'Share the outcome you need and any details that will help us understand the request.'
      };

      const hint = hints[this.topicSelect.value];
      this.topicHint.textContent = hint || 'Choose a topic to see which details will help us answer.';
      this.topicHint.dataset.active = String(Boolean(hint));
      const productField = this.querySelector('[data-evolved-contact-product-field]');
      if (productField) {
        const hasValue = Boolean(productField.querySelector('input')?.value.trim());
        productField.hidden = !hasValue && !['Compatibility question', 'Product issue', 'Return or refund', 'Other'].includes(this.topicSelect.value);
      }
    }
  }

  class EvolvedPolicyLayout extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      const content = this.querySelector('.evolved-support__policy-content');
      if (!content) return;

      this.content = content;
      this.headings = Array.from(content.querySelectorAll('section[id]'));
      this.navLinks = Array.from(this.querySelectorAll('[data-evolved-policy-link]'));
      const usedIds = new Set();

      this.headings.forEach((heading, index) => {
        let id = heading.id || slugify(heading.textContent);
        if (!id) id = `policy-section-${index + 1}`;

        const baseId = id;
        let suffix = 2;
        while (usedIds.has(id)) {
          id = `${baseId}-${suffix}`;
          suffix += 1;
        }

        usedIds.add(id);
        heading.id = id;
      });

      this.navLinks.forEach((link) => {
        const targetId = link.getAttribute('href')?.slice(1);
        link.hidden = !targetId || !this.querySelector(`#${CSS.escape(targetId)}`);
      });

      const jumpShell = this.querySelector('.evolved-support__policy-jumps-shell');
      if (jumpShell) {
        const hasAvailableJump = this.navLinks.some(
          (link) => link.closest('.evolved-support__policy-jumps-shell') && !link.hidden
        );
        jumpShell.hidden = !hasAvailableJump;
      }

      if ('IntersectionObserver' in window) {
        const linkedIds = new Set(
          this.navLinks
            .filter((link) => !link.hidden)
            .map((link) => link.getAttribute('href')?.slice(1))
            .filter(Boolean)
        );
        this.observer = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
            if (visible) this.setActiveLink(visible.target.id);
          },
          { rootMargin: '-18% 0px -68% 0px', threshold: [0, 1] }
        );
        this.headings
          .filter((heading) => linkedIds.has(heading.id))
          .forEach((heading) => this.observer.observe(heading));
      }

      this.frame = 0;
      const updateProgress = () => {
        this.frame = 0;
        const rect = content.getBoundingClientRect();
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        const traveled = Math.max(0, -rect.top + viewport * 0.35);
        const available = Math.max(1, rect.height - viewport * 0.45);
        const progress = Math.min(100, Math.max(0, (traveled / available) * 100));
        this.style.setProperty('--es-policy-progress', `${progress.toFixed(2)}%`);
      };

      window.addEventListener(
        'scroll',
        () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(updateProgress);
        },
        { passive: true, signal }
      );
      window.addEventListener('resize', updateProgress, { passive: true, signal });
      updateProgress();
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();
      this.observer = null;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    setActiveLink(id) {
      this.navLinks.forEach((link) => {
        if (link.getAttribute('href') === `#${id}`) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }
  }

  if (!customElements.get('evolved-support-faq')) {
    customElements.define('evolved-support-faq', EvolvedSupportFaq);
  }

  if (!customElements.get('evolved-support-contact')) {
    customElements.define('evolved-support-contact', EvolvedSupportContact);
  }

  if (!customElements.get('evolved-policy-layout')) {
    customElements.define('evolved-policy-layout', EvolvedPolicyLayout);
  }
})();
